import React, { useEffect, useRef, useState } from 'react';
import { FaLightbulb, FaMapMarkedAlt, FaNetworkWired, FaPuzzlePiece, FaQuoteLeft, FaFlask } from 'react-icons/fa';
import { FiClock } from 'react-icons/fi';
import { Container } from '../../globalStyles';
import {
  AchievementSec,
  Glow,
  Reveal,
  Intro,
  TopLine,
  Heading,
  GradientText,
  Lead,
  StoryRow,
  MapWrap,
  MapGlow,
  MapRing,
  MapFloat,
  MapImg,
  Hub,
  Chip,
  IconBox,
  ChipFigure,
  ChipLabel,
  SubHeading,
  Text,
  TextLink,
  StatsLabel,
  StatsGrid,
  StatCard,
  StatTop,
  StatFigure,
  StatBigIcon,
  CardsRow,
  StoryCard,
  StepNumber,
  Closing,
  QuoteMark,
  Quote,
  Pillars,
  Pillar
} from './Achievement.elements';

const icons = {
  network: FaNetworkWired,
  clock: FiClock,
  flask: FaFlask,
  map: FaMapMarkedAlt
};

/* Hub positions over the India art (percent of the image box), on its
   brightest nodes so the pulses read as the network lighting up. */
const hubs = [
  { left: '38%', top: '26%' },
  { left: '20%', top: '44%' },
  { left: '43%', top: '50%' },
  { left: '35%', top: '64%' },
  { left: '88%', top: '34%' },
  { left: '33%', top: '86%' }
];

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/* True once the element has scrolled into view. Without
   IntersectionObserver everything is shown straight away. */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(
    typeof window !== 'undefined' && !('IntersectionObserver' in window)
  );

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, threshold]);

  return [ref, inView];
}

function FadeIn({ children, from, delay, fill }) {
  const [ref, inView] = useInView();
  return (
    <Reveal ref={ref} $visible={inView} $from={from} $delay={delay} $fill={fill}>
      {children}
    </Reveal>
  );
}

/* Counts up to `value` the first time it is seen; under reduced motion it
   shows the final number at once. */
function CountUp({ value, prefix = '', suffix = '' }) {
  const [ref, inView] = useInView(0.5);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    if (prefersReducedMotion()) {
      setCount(value);
      return undefined;
    }

    let frame;
    const start = performance.now();
    const duration = 1600;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      setCount(Math.round(value * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toLocaleString('en-IN')}
      {suffix}
    </span>
  );
}

/* Renders Data.js copy: plain strings, { strong } bold runs and
   { strong, href } links. */
function RichText({ value }) {
  return value.map((run, i) =>
    typeof run === 'string' ? (
      <React.Fragment key={i}>{run}</React.Fragment>
    ) : run.href ? (
      <TextLink key={i} href={run.href} target='_blank' rel='noopener noreferrer'>
        {run.strong}
      </TextLink>
    ) : (
      <strong key={i}>{run.strong}</strong>
    )
  );
}

function FigureChip({ stat, placement, duration, delay }) {
  const Icon = icons[stat.icon];
  const label = stat.line.find((run) => typeof run !== 'string');

  // The stat cards carry the same facts, so the chip is hidden from
  // screen readers rather than read twice.
  return (
    <Chip aria-hidden='true' $placement={placement} $duration={duration} $delay={delay}>
      <IconBox $size={36}>
        <Icon />
      </IconBox>
      <span>
        {stat.figure && (
          <ChipFigure>
            <CountUp {...stat.figure} />
          </ChipFigure>
        )}
        {label && <ChipLabel>{label.strong}</ChipLabel>}
      </span>
    </Chip>
  );
}

function Achievement({
  topLine,
  tagline,
  lead,
  headline,
  body,
  img,
  alt,
  challenge,
  approach,
  statsHeading,
  stats,
  demonstrates,
  closing,
  pillars
}) {
  const cards = [
    { block: challenge, Icon: FaPuzzlePiece, step: '01', from: 'left' },
    { block: approach, Icon: FaLightbulb, step: '02', from: 'right' }
  ];

  return (
    <AchievementSec id='achievement'>
      <Glow aria-hidden='true' $size='560px' $top='-180px' $left='-200px' $color='rgba(75, 89, 247, 0.22)' />
      <Glow aria-hidden='true' $size='480px' $top='35%' $left='70%' $color='rgba(56, 189, 248, 0.14)' />

      <Container>
        <FadeIn>
          <Intro>
            <TopLine>{topLine}</TopLine>
            <Heading>
              {tagline[0]} <GradientText>{tagline[1]}</GradientText>
            </Heading>
            <Lead>{lead}</Lead>
          </Intro>
        </FadeIn>

        <StoryRow>
          <FadeIn from='scale'>
            <MapWrap>
              <MapGlow aria-hidden='true' />
              {[0, 1.6, 3.2].map((delay) => (
                <MapRing key={delay} aria-hidden='true' $delay={delay} />
              ))}
              <MapFloat>
                <MapImg src={img} alt={alt} width='313' height='348' loading='lazy' />
                {hubs.map((hub, i) => (
                  <Hub key={i} aria-hidden='true' style={hub} $delay={i * 0.4} />
                ))}
              </MapFloat>
              <FigureChip stat={stats[0]} placement='top' duration={5} />
              <FigureChip stat={stats[1]} placement='bottom' duration={5.5} delay={0.8} />
            </MapWrap>
          </FadeIn>

          <div>
            <FadeIn from='right'>
              <SubHeading>{headline}</SubHeading>
              <Text>
                <RichText value={body} />
              </Text>
            </FadeIn>

            <StatsLabel>{statsHeading}</StatsLabel>
            <StatsGrid>
              {stats.map((stat, i) => {
                const Icon = icons[stat.icon];
                return (
                  <FadeIn key={i} delay={i * 0.1} fill>
                    <StatCard>
                      <StatTop>
                        {stat.figure ? (
                          <>
                            <StatFigure aria-hidden='true'>
                              <CountUp {...stat.figure} />
                            </StatFigure>
                            <IconBox>
                              <Icon aria-hidden='true' />
                            </IconBox>
                          </>
                        ) : (
                          <StatBigIcon aria-hidden='true'>
                            <Icon />
                          </StatBigIcon>
                        )}
                      </StatTop>
                      <Text $small $mt={12}>
                        <RichText value={stat.line} />
                      </Text>
                    </StatCard>
                  </FadeIn>
                );
              })}
            </StatsGrid>
          </div>
        </StoryRow>

        <CardsRow>
          {cards.map(({ block, Icon, step, from }) => (
            <FadeIn key={step} from={from} fill>
              <StoryCard>
                <StepNumber aria-hidden='true'>{step}</StepNumber>
                <IconBox $size={48} $solid>
                  <Icon aria-hidden='true' />
                </IconBox>
                <SubHeading $small style={{ marginTop: 24 }}>
                  {block.heading}
                </SubHeading>
                <Text>
                  <RichText value={block.body} />
                </Text>
              </StoryCard>
            </FadeIn>
          ))}
        </CardsRow>

        <FadeIn>
          <Closing>
            <SubHeading $small>{demonstrates.heading}</SubHeading>
            <Text>{demonstrates.body}</Text>

            <figure>
              <QuoteMark aria-hidden='true'>
                <FaQuoteLeft />
              </QuoteMark>
              <Quote>{closing}</Quote>
            </figure>

            <Pillars>
              {pillars.map((pillar) => (
                <Pillar key={pillar}>{pillar}</Pillar>
              ))}
            </Pillars>
          </Closing>
        </FadeIn>
      </Container>
    </AchievementSec>
  );
}

export default Achievement;
